import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

// Phase 1 — public marketing pages
import Landing from "@/pages/Landing";
import LandingAr from "@/pages/LandingAr";
import About from "@/pages/About";
import Gathering from "@/pages/Gathering";
import Membership from "@/pages/Membership";
import Sponsors from "@/pages/Sponsors";
import Rules from "@/pages/Rules";
import Culture from "@/pages/Culture";
import CrewzApp from "@/pages/CrewzApp";
import Privacy from "@/pages/Privacy";
import AccountDeletion from "@/pages/AccountDeletion";
import Contact from "@/pages/Contact";
import Store from "@/pages/Store";
import BuildDetail from "@/pages/BuildDetail";
import AdminBuilds from "@/pages/AdminBuilds";
import SetupInstagram from "@/pages/SetupInstagram";
import AdminReset from "@/pages/AdminReset";
import ArrivalPrototype from "@/pages/ArrivalPrototype";
import AmbientPrototype from "@/pages/AmbientPrototype";
import VisualHeaderPrototype from "@/pages/VisualHeaderPrototype";
import CinematicHeaderPrototype from "@/pages/CinematicHeaderPrototype";
import IntegratedRibbonPrototype from "@/pages/IntegratedRibbonPrototype";
import ImageRevealPrototype from "@/pages/ImageRevealPrototype";
import PhotographicDestinationsPrototype from "@/pages/PhotographicDestinationsPrototype";
import ScrollToTop from "@/components/ScrollToTop";
import LangDirSync from "@/components/LangDirSync";
import Terms from "@/pages/Terms";

// Phase 3 — community features (kept for future, not in primary nav)
// Note: Register was withdrawn from the public site in Milestone 1
// (Feb 2026). Membership is the institution's sole public entry point;
// account registration returns as part of the authenticated member
// platform in Phase 2. The page is preserved under `pages/_future/`.
import Login from "@/pages/Login";
import Feed from "@/pages/Feed";
import Crews from "@/pages/Crews";
import CrewDetail from "@/pages/CrewDetail";
import Profile from "@/pages/Profile";
import ProtectedRoute from "@/components/ProtectedRoute";

function App() {
    return (
        <div className="App grain min-h-screen bg-[var(--crewz-bg)] text-zinc-100 font-body">
            <BrowserRouter>
                <AuthProvider>
                    <ScrollToTop />
                    <LangDirSync />
                    <Routes>
                        {/* Phase 1 */}
                        <Route path="/" element={<Landing />} />
                        <Route path="/ar" element={<LandingAr />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/gathering" element={<Gathering />} />

                        {/* CULTURE — parent experience that composes
                            EVENTS, GALLERY and SOCIAL as three visually
                            distinct chapters. Legacy top-level routes
                            below redirect to their anchored section
                            here so external links continue to resolve. */}
                        <Route path="/culture" element={<Culture />} />
                        <Route path="/ar/culture" element={<Culture locale="ar" />} />

                        {/* CREWZ APP — premium teaser for the CrewZNatioN
                            mobile application. Countdown is dormant
                            until an owner-supplied launch date is set. */}
                        <Route path="/app" element={<CrewzApp />} />
                        <Route path="/ar/app" element={<CrewzApp locale="ar" />} />

                        {/* Legacy top-level nav — kept as clean anchor
                            redirects into Culture. Do NOT re-mount the
                            standalone pages here; they render inside
                            Culture with `embedded={true}`. */}
                        <Route path="/events" element={<Navigate to="/culture#events" replace />} />
                        <Route path="/gallery" element={<Navigate to="/culture#gallery" replace />} />
                        <Route path="/social" element={<Navigate to="/culture#social" replace />} />
                        <Route path="/ar/events" element={<Navigate to="/ar/culture#events" replace />} />
                        <Route path="/ar/gallery" element={<Navigate to="/ar/culture#gallery" replace />} />
                        <Route path="/ar/social" element={<Navigate to="/ar/culture#social" replace />} />

                        <Route path="/membership" element={<Membership />} />
                        <Route path="/sponsors" element={<Sponsors />} />
                        <Route path="/store" element={<Store />} />
                        <Route path="/builds/:slug" element={<BuildDetail />} />
                        <Route path="/admin/builds" element={<AdminBuilds />} />
                        <Route path="/setup/instagram" element={<SetupInstagram />} />
                        <Route path="/admin/reset" element={<AdminReset />} />
                        <Route path="/rules" element={<Rules />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/account-deletion" element={<AccountDeletion />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/contact" element={<Contact />} />
                        {/* /support is a clean permanent alias of /contact for
                            App Store / Play Console reviewers who expect the
                            support URL to live at /support. Kept as a redirect
                            (not a duplicate page) so there is only one source
                            of support content. */}
                        <Route path="/support" element={<Navigate to="/contact" replace />} />

                        {/* Arabic scaffolding (Feb 2026) — each Arabic route
                            passes locale="ar" to the shared page component,
                            which mirrors layout for RTL via NavbarAr/FooterAr
                            and dir="rtl" on the outer container. Temporary
                            English content until Arabic editorial packages
                            are approved section by section. */}
                        <Route path="/ar/about" element={<About locale="ar" />} />
                        <Route path="/ar/gathering" element={<Gathering locale="ar" />} />
                        <Route path="/ar/sponsors" element={<Sponsors locale="ar" />} />
                        <Route path="/ar/store" element={<Store locale="ar" />} />
                        <Route path="/ar/contact" element={<Contact locale="ar" />} />
                        <Route path="/ar/membership" element={<Membership />} />
                        <Route path="/ar/rules" element={<Rules locale="ar" />} />
                        <Route path="/ar/privacy" element={<Privacy locale="ar" />} />
                        <Route path="/ar/account-deletion" element={<AccountDeletion locale="ar" />} />
                        <Route path="/ar/terms" element={<Terms locale="ar" />} />
                        <Route path="/ar/support" element={<Navigate to="/ar/contact" replace />} />

                        {/* Experimental prototypes — not part of the approved bilingual site */}
                        <Route path="/prototypes/arrival" element={<ArrivalPrototype />} />
                        <Route path="/prototypes/ambient" element={<AmbientPrototype />} />
                        <Route path="/prototypes/visual-header" element={<VisualHeaderPrototype />} />
                        <Route path="/prototypes/cinematic-header" element={<CinematicHeaderPrototype />} />
                        <Route path="/prototypes/integrated-ribbon" element={<IntegratedRibbonPrototype />} />
                        <Route path="/prototypes/image-reveal-header" element={<ImageRevealPrototype />} />
                        <Route path="/prototypes/photographic-destinations" element={<PhotographicDestinationsPrototype />} />

                        {/* Phase 3 (preserved) */}
                        <Route path="/login" element={<Login />} />
                        <Route path="/feed" element={<Feed />} />
                        <Route path="/crews" element={<Crews />} />
                        <Route path="/crews/:slug" element={<CrewDetail />} />
                        <Route path="/u/:username" element={<Profile />} />
                        <Route
                            path="/me"
                            element={
                                <ProtectedRoute>
                                    <Profile />
                                </ProtectedRoute>
                            }
                        />
                    </Routes>
                    <Toaster richColors position="top-right" />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
